import { Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogPanel, DialogTitle } from "@/components/ui/dialog";

import { type ClaimWithRelations, useClaim } from "../../api/use-claim";
import type { AssessmentMetadata } from "../../api/use-claim-insights";
import { getAiEligibilityVerdict } from "../../lib/ai-verdict";
import { isPartnerFeedOffice } from "../../lib/partner-feed";
import { getRelevantCx } from "../circumstances/claim-circumstances";
import { SelectAbandonType } from "../select-abandon-type";
import { hasNegativeCriteria } from "./ai-insights-panel";

const CX_ABANDON_TYPE_ID = 1;
const FALSE_POSITIVE_ABANDON_TYPE_ID = 12; // ALGO_FALSE_POSITIVE — "Faux positif"

/**
 * Most likely abandon type given the AI study. Non éligible with a detected CX → CX,
 * unless a criterion "en défaut" (e.g. origine volontaire suspectée) points to a false
 * positive instead; any other verdict → false positive; no study → no preselection.
 */
export function getDefaultAbandonTypeId(claim: ClaimWithRelations | undefined): number | undefined {
  const isEligible = getAiEligibilityVerdict(claim);
  if (isEligible === undefined) {
    // Digitrips/partner-feed claims have no Amadeus PNR, so no AI study/verdict ever
    // exists. Preselect the generic "faux positif" default for these algo-generated
    // claims instead of leaving the field empty (TECH-994).
    if (
      isPartnerFeedOffice(
        claim?.legalSpecification?.impactedSegment?.reservation?.travelAgencyOffice?.amadeusOfficeId,
      )
    ) {
      return FALSE_POSITIVE_ABANDON_TYPE_ID;
    }
    return undefined;
  }
  if (isEligible) {
    return FALSE_POSITIVE_ABANDON_TYPE_ID;
  }

  const assessment = claim?.insights?.find((insight) => insight?.type?.type === "ASSESSMENT");
  const assessmentMetadata = assessment?.metadata as AssessmentMetadata | null | undefined;
  const hasCx =
    assessmentMetadata?.preprocess?.remarks?.hasExtraordinaryCircumstanceHint === true ||
    getRelevantCx(claim).active.length > 0;
  return hasCx && !hasNegativeCriteria(assessmentMetadata ?? null)
    ? CX_ABANDON_TYPE_ID
    : FALSE_POSITIVE_ABANDON_TYPE_ID;
}

interface AbandonClaimButtonProps {
  claimId: string;
  className?: string;
  size?: "default" | "sm" | "lg";
  /** Red when abandoning is the expected next step, ghost when it is only an option. */
  variant?: "destructive" | "ghost";
}

/** « Abandonner » opening the abandon form with the most likely type preselected. */
export function AbandonClaimButton({
  claimId,
  className,
  size,
  variant = "destructive",
}: AbandonClaimButtonProps) {
  const { data: claim } = useClaim(claimId);
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button variant={variant} size={size} className={className} onClick={() => setOpen(true)}>
        <Trash2 />
        Abandonner
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Abandonner le dossier</DialogTitle>
          </DialogHeader>
          <DialogPanel>
            {open ? (
              <SelectAbandonType
                claimId={claimId}
                defaultAbandonTypeId={getDefaultAbandonTypeId(claim)}
                onUpdated={() => setOpen(false)}
              />
            ) : null}
          </DialogPanel>
        </DialogContent>
      </Dialog>
    </>
  );
}
